import { fetchWithSession } from './fetchWithSession';

const TTL_MS = 10 * 60 * 1000;
const MAX_ENTRIES = 16;
const MAX_BYTES = 256 * 1024 * 1024;

interface StoredGlb {
  blob: Blob;
  expiresAt: number;
}

const stored = new Map<string, StoredGlb>();
const inFlight = new Map<string, Promise<Blob>>();
let storedBytes = 0;
let cacheSessionId: string | null = null;

function drop(url: string): void {
  const entry = stored.get(url);
  if (!entry) return;
  storedBytes -= entry.blob.size;
  stored.delete(url);
}

function store(url: string, blob: Blob): void {
  if (blob.size > MAX_BYTES) return;
  drop(url);
  stored.set(url, { blob, expiresAt: Date.now() + TTL_MS });
  storedBytes += blob.size;
  while (stored.size > MAX_ENTRIES || storedBytes > MAX_BYTES) {
    const oldest = stored.keys().next();
    if (oldest.done) break;
    drop(oldest.value);
  }
}

async function fetchGlb(url: string): Promise<Blob> {
  const token = sessionStorage.getItem('access_token');
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  const response = await fetchWithSession(url, { headers });
  if (!response.ok) {
    throw Object.assign(new Error(`GLB load failed: ${response.status}`), {
      code: `HTTP_${response.status}`,
    });
  }
  return response.blob();
}

/**
 * Load a CAD GLB for display, reusing bytes from this login session within the TTL and size bounds.
 *
 * Concurrent callers share one request; a failed request is never reused. Entries are scoped to
 * `auth_session_id`: a new login, user or tenant never sees them, and with no session it fetches uncached.
 */
export function loadGlbBlob(url: string): Promise<Blob> {
  const sessionId = sessionStorage.getItem('auth_session_id');
  if (!sessionId) return fetchGlb(url);
  if (sessionId !== cacheSessionId) {
    clearGlbCache();
    cacheSessionId = sessionId;
  }

  const hit = stored.get(url);
  if (hit && hit.expiresAt >= Date.now()) {
    // Re-insert so Map order stays least recently used first (store() evicts from the front).
    stored.delete(url);
    stored.set(url, hit);
    return Promise.resolve(hit.blob);
  }
  drop(url);

  const pending = inFlight.get(url);
  if (pending) return pending;

  const request = fetchGlb(url).then(
    (blob) => {
      // A logout or eviction while in flight keeps these bytes out; a session change is caught on the next read.
      if (inFlight.get(url) === request) {
        inFlight.delete(url);
        store(url, blob);
      }
      return blob;
    },
    (error) => {
      if (inFlight.get(url) === request) inFlight.delete(url);
      throw error;
    },
  );
  inFlight.set(url, request);
  return request;
}

/** Forget every GLB whose URL starts with `prefix`, e.g. all renders of a deleted model. */
export function evictGlbsUnder(prefix: string): void {
  [...stored.keys()].filter((url) => url.startsWith(prefix)).forEach(drop);
  [...inFlight.keys()].filter((url) => url.startsWith(prefix)).forEach((url) => inFlight.delete(url));
}

/** Forget every GLB, e.g. on logout. */
export function clearGlbCache(): void {
  stored.clear();
  inFlight.clear();
  storedBytes = 0;
  cacheSessionId = null;
}
